// Contract 29 §8 — `CreateWorkspaceFlow`. Ports `OnboardingStep1.vue:41-95`.
//
// Preserved verbatim: brand-context failure is NON-fatal (§14.1) and workspace-create failure
// aborts (§14.2). Dropped: `swalAlert`, `appAxios` in an SFC, and the global loader.

import { OnboardingApi } from '../api/OnboardingApi'
import type { WorkspaceBrandDefaults } from '../models/onboarding.types'

import { workspaceAttributeWriter } from './attributeWriterRegistry'

import type { ApiError } from '@/shared/http'

export type CreateWorkspaceResult =
  { ok: true; workspaceId: number } | { ok: false; errorKey: string; detail?: string }

export interface CreateWorkspaceInput {
  name: string
  domain: string
  mcpOnly?: boolean
  reuseWorkspaceId?: number
}

export interface CreateWorkspaceDependencies {
  api?: OnboardingApi
  accountId: () => number | undefined
  /** 07's list refresh + selection — the new workspace must be current before step 2 writes. */
  refreshWorkspaces: () => Promise<void>
  selectWorkspace: (workspaceId: number) => Promise<void>
  setDefaults: (defaults: WorkspaceBrandDefaults | null) => void
  setCreatedWorkspace: (workspaceId: number) => void
  /** ORP-24218: only the first Google signup may replace the placeholder account name. */
  isGoogleFirstWorkspace?: () => boolean
  /** Fire-and-forget rejections land here instead of the void (§6.3). */
  report?: (scope: string, error: unknown) => void
}

/** The browser-derived `UTC±H` hint the legacy request carried (`OnboardingStep1.vue:45-46`). */
export function browserUtcOffset(date: Date = new Date()): string {
  const offset = -date.getTimezoneOffset() / 60
  return `UTC${offset >= 0 ? '+' : ''}${offset}`
}

export function browserLanguage(): string {
  const languages = navigator.languages
  return (Array.isArray(languages) && languages[0]) || navigator.language || 'en-US'
}

export class CreateWorkspaceFlow {
  private readonly api: OnboardingApi
  private readonly deps: CreateWorkspaceDependencies

  constructor(deps: CreateWorkspaceDependencies) {
    this.api = deps.api ?? new OnboardingApi()
    this.deps = deps
  }

  async execute(input: CreateWorkspaceInput): Promise<CreateWorkspaceResult> {
    const accountId = this.deps.accountId()
    if (accountId === undefined) {
      return { ok: false, errorKey: 'globalSurface.onboarding.workspace.createFailed' }
    }

    const name = input.name.trim()
    const domain = input.domain.trim()

    // Both calls are issued together, as legacy did; only the CREATE half may fail the flow.
    const [created, brandContext] = await Promise.allSettled([
      input.reuseWorkspaceId !== undefined
        ? this.api
            .updateWorkspaceSettings(accountId, input.reuseWorkspaceId, { name })
            .then(() => input.reuseWorkspaceId as number)
        : this.api.createWorkspace(accountId, name, input.mcpOnly === true),
      this.api.brandContextProfile(accountId, {
        domain,
        timezone: browserUtcOffset(),
        language: browserLanguage(),
        workspace_name: name,
      }),
    ])

    if (brandContext.status === 'fulfilled') this.deps.setDefaults(brandContext.value)
    else this.deps.report?.('onboarding.brand_context', brandContext.reason)

    if (created.status === 'rejected') {
      const error = created.reason as Partial<ApiError>
      return {
        ok: false,
        errorKey: 'globalSurface.onboarding.workspace.createFailed',
        ...(typeof error?.detail === 'string' ? { detail: error.detail } : {}),
      }
    }
    const workspaceId = created.value
    if (workspaceId === null) {
      return { ok: false, errorKey: 'globalSurface.onboarding.workspace.createFailed' }
    }

    this.deps.setCreatedWorkspace(workspaceId)

    if (this.deps.isGoogleFirstWorkspace?.()) {
      try {
        await this.api.renameAccount(accountId, name)
      } catch (error: unknown) {
        this.deps.report?.('onboarding.account_name_sync', error)
      }
    }
    await this.deps.refreshWorkspaces()
    await this.deps.selectWorkspace(workspaceId)

    // Fire-and-forget by design (§6.3) — the rejection is reported, never surfaced.
    void this.api
      .provisionServiceAccount(workspaceId)
      .catch((error: unknown) => this.deps.report?.('onboarding.service_account', error))

    // `main_domain` is the one attribute step 1 writes, against a workspace that has only just
    // become selectable. A missing writer is a pre-cutover state, not a create failure.
    const writer = workspaceAttributeWriter()
    if (writer) {
      try {
        await writer({ key: 'main_domain', value: domain, attributeId: null, workspaceId })
      } catch (error: unknown) {
        this.deps.report?.('onboarding.main_domain', error)
      }
    }

    return { ok: true, workspaceId }
  }
}
